import { createContext, useContext } from 'react'
import type { User, Workspace } from '@core/api/auth'
import type { Partilhados } from '@core/partilhas/partilhasRegras'

/**
 * Runtime modes:
 *
 * - `cloud`: backend reachable AND user has a valid session.
 * - `unauthenticated`: backend reachable but no session, redirect to /login.
 * - `local`: backend unreachable (e.g. user opened the SPA via run.bat
 *   with no FastAPI running), or the user chose to carry on offline
 *   after a server error. Local-first viewer with no auth UI.
 * - `forbidden`: the server answered 403 (workspace archived). Shows a
 *   blocking screen instead of silently dropping into local mode.
 * - `server-error`: the server answered 5xx or something unreadable.
 *   Shows "Servidor indisponível" with retry.
 *
 * `loading` is the initial state while the boot probe is in flight.
 */
export type AuthMode =
  | 'loading'
  | 'cloud'
  | 'unauthenticated'
  | 'local'
  | 'forbidden'
  | 'server-error'

export interface AuthState {
  mode: AuthMode
  user: User | null
  workspace: Workspace | null
  modules: string[]
  /** Recursos partilhados com a pessoa a partir de outros workspaces.
   *  Mostram as partes da interface precisas para os abrir; o servidor
   *  verifica cada recurso. */
  partilhados?: Partilhados
  hasPassword: boolean
  /** Why the user is out: a code from `core/auth/avisos.ts` for the
   *  login page, or the server's message on the blocking screens. */
  aviso: string | null
}

export interface AuthContextValue extends AuthState {
  refresh: () => Promise<void>
  logout: () => Promise<void>
  /** After a server error, carry on with the local-first viewer. */
  continuarLocal: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
